import { Injectable } from '@angular/core';
import { DoctorFacade } from '@modules/doctor/doctor.facade';
import { DoctorQuery } from '@modules/doctor/state/doctor-query';
import { DoctorStore } from '@modules/doctor/state/doctor-store';
import { LoggerService } from '@core/services/logger/logger.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ShellStore } from '@shared/store/shell/shell-store';
import { DoctorsService } from '@modules/doctor/services/doctors.service';
import { Observable, of, combineLatest } from 'rxjs';
import { catchError, shareReplay, map, startWith, switchMap, distinctUntilChanged, withLatestFrom } from 'rxjs/operators';
import { Doctor } from '@shared/models/doctor';
import { allowedProceduresForReassign } from '@shared/models/consts';
/**
 * Facade for the rx-intuitive doctor license UI. Extends DoctorFacade and overrides
 * updateLicense to also sync the license to DoctorStore and set isLicenseEditable.
 */
@Injectable()
export class RxIntuitiveDoctorLicenseFacade extends DoctorFacade {
	isResendScan$: Observable<boolean> = this.shellQuery.isResendScan$;

	private readonly procedureId$: Observable<number | undefined> = this.shellQuery.rx$.pipe(
		map(rx => rx?.Order?.ProcedureId),
		distinctUntilChanged()
	);

	readonly doctors$: Observable<Doctor[]> = combineLatest([
		this.shellQuery.companyId$,
		this.procedureId$
	]).pipe(
		withLatestFrom(this.isResendScan$, this.query.doctor$),
		switchMap(([[companyId, procedureId], isResend, storeDoctor]) => {
			const fallback = this.includeFallbackDoctor([], storeDoctor);

			if (!isResend || !companyId || !procedureId) {
				return of(fallback);
			}

			return this.doctorsService.getDoctors(companyId, procedureId).pipe(
				map(doctors => doctors.filter(doctor => !!doctor.Licence)),
				map(doctors => this.includeFallbackDoctor(doctors, storeDoctor)),
				catchError(error => {
					this.logger.error('Failed to load doctors list', {
						module: 'RxIntuitiveDoctorLicenseFacade',
						extendedParameters: { error }
					});

					return of(fallback);
				}),
				startWith(fallback)
			);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly defaultSelectedDoctor$: Observable<Doctor | null> = combineLatest([this.doctors$, this.query.doctor$]).pipe(
		map(([doctors, currentDoctor]) => {
			if (!currentDoctor?.Id || !doctors?.length) {
				return null;
			}

			return doctors.find(doctor => doctor.Id === currentDoctor.Id) || null;
		})
	);

	readonly isAllowedProcedure$: Observable<boolean> = this.shellQuery.rx$.pipe(
		map(rx => !!rx?.Order?.ProcedureId && allowedProceduresForReassign.includes(rx.Order.ProcedureId)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly canReassignScan$: Observable<boolean> = combineLatest([this.isResendScan$, this.isAllowedProcedure$]).pipe(
		map(([isResendScan, isAllowedProcedure]) => isResendScan && isAllowedProcedure),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	constructor(
		store: DoctorStore,
		query: DoctorQuery,
		shellStore: ShellStore,
		shellQuery: ShellQuery,
		logger: LoggerService,
		private readonly doctorsService: DoctorsService
	) {
		super(store, query, shellStore, shellQuery, logger);
	}

	override updateLicense({ license }: { license: string }): void {
		super.updateLicense({ license });

		const current = this.store.getValue().doctor;

		if (current) {
			this.store.update({ doctor: { ...current, Licence: license || '' } });
		}

		if (license) {
			this.store.update({ isLicenseEditable: false });
		}
	}

	private includeFallbackDoctor(doctors: Doctor[], storeDoctor: Doctor | null): Doctor[] {
		if (!storeDoctor) {
			return doctors;
		}

		const alreadyIncluded = doctors.some(doctor => doctor.Id === storeDoctor.Id);

		return alreadyIncluded ? doctors : [storeDoctor, ...doctors];
	}
}
